import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';

export const END_OF_DATA_RECOVERY_LOG_INTERVAL_MS = 30_000;

export type EndOfDataRecoveryRetryReason = 'replacement-end-of-data' | 'refetch-failed';

export type EndOfDataRecoveryCancellationReason =
    | 'destroyed'
    | 'manual-refetch'
    | 'model-disposed'
    | 'no-query'
    | 'proxy-target-changed'
    | 'query-disabled'
    | 'query-options-changed'
    | 'superseded';

export type EndOfDataRecoveryLogEvent =
    | {
          type: 'incident-started';
          tableKey: string | null;
          debugIdentifier: string | undefined;
      }
    | { type: 'end-of-data-coalesced'; reason: 'in-flight' | 'retry-scheduled' }
    | { type: 'replacement-end-of-data' }
    | { type: 'recovery-attempt'; attempt: number }
    | {
          type: 'retry-scheduled';
          reason: EndOfDataRecoveryRetryReason;
          attempt: number;
          retryInMs: number;
      }
    | {
          type: 'refetch-failed';
          failure: 'result' | 'threw';
          error: Error | undefined;
      }
    | {
          type: 'replacement-created';
          result: 'same-key' | 'new-key';
          newTableKey?: string;
      }
    | { type: 'healthy'; evidence: 'descriptor' | 'stable' }
    | { type: 'canceled'; reason: EndOfDataRecoveryCancellationReason }
    | { type: 'still-recovering' };

export type EndOfDataRecoveryLogFlush = 'none' | 'before' | 'after' | 'before-and-after';

type EndOfDataRecoveryLogPhase = 'started' | 'progress' | 'still-recovering' | 'recovered' | 'canceled';

interface EndOfDataRecoveryLogState {
    readonly incidentId: string;
    readonly startedAt: number;
    readonly tableKey: string | null;
    readonly debugIdentifier: string | undefined;
    phase: EndOfDataRecoveryLogPhase;
    pendingEvents: number;
    endOfDataEvents: number;
    coalescedEndOfDataEvents: number;
    coalescedInFlightEvents: number;
    coalescedScheduledEvents: number;
    recoveryAttempts: number;
    retriesScheduled: number;
    refetchFailures: number;
    refetchThrows: number;
    sameKeyReplacements: number;
    newKeyReplacements: number;
    maxRetryInMs: number;
    lastRetryInMs: number | undefined;
    lastRetryReason: EndOfDataRecoveryRetryReason | undefined;
    lastError: Error | undefined;
    lastEventType: EndOfDataRecoveryLogEvent['type'];
    newTableKey: string | undefined;
    healthEvidence: 'descriptor' | 'stable' | undefined;
    cancellationReason: EndOfDataRecoveryCancellationReason | undefined;
}

export interface EndOfDataRecoveryLogAggregatorOptions {
    flushIntervalMs?: number;
    now?: () => number;
}

const increment = (value: number): number => Math.min(value + 1, Number.MAX_SAFE_INTEGER);

/**
 * Per-TableModel recovery diagnostics. Incoming events update one incident state and are emitted as
 * bounded summaries instead of one Datadog log per wire event. Leading and terminal events can
 * flush around their state transition so summaries retain causal ordering.
 */
export class EndOfDataRecoveryLogAggregator implements Disposable {
    readonly #flushIntervalMs: number;
    readonly #log: Logger;
    readonly #now: () => number;
    readonly #tableModelId: string;
    #disposed = false;
    #flushTimer: ReturnType<typeof setTimeout> | undefined;
    #incidentSequence = 0;
    #state: EndOfDataRecoveryLogState | undefined;

    constructor(log: Logger, tableModelId: string, options: EndOfDataRecoveryLogAggregatorOptions = {}) {
        this.#flushIntervalMs = options.flushIntervalMs ?? END_OF_DATA_RECOVERY_LOG_INTERVAL_MS;
        if (!Number.isSafeInteger(this.#flushIntervalMs) || this.#flushIntervalMs < 1) {
            throw new Error('End-of-data recovery log interval must be a positive safe integer');
        }
        this.#log = log;
        this.#now = options.now ?? Date.now;
        this.#tableModelId = tableModelId;
    }

    update(event: EndOfDataRecoveryLogEvent, flush: EndOfDataRecoveryLogFlush = 'none'): void {
        if (this.#disposed) {
            return;
        }

        if (flush === 'before' || flush === 'before-and-after') {
            this.#flush();
        }

        if (event.type === 'incident-started') {
            if (this.#state) {
                this.#applyEvent({ type: 'canceled', reason: 'superseded' });
                this.#flush();
            }
            this.#incidentSequence += 1;
            this.#state = {
                incidentId: `${this.#tableModelId}:${this.#incidentSequence}`,
                startedAt: this.#now(),
                tableKey: event.tableKey,
                debugIdentifier: event.debugIdentifier,
                phase: 'started',
                pendingEvents: 0,
                endOfDataEvents: 0,
                coalescedEndOfDataEvents: 0,
                coalescedInFlightEvents: 0,
                coalescedScheduledEvents: 0,
                recoveryAttempts: 0,
                retriesScheduled: 0,
                refetchFailures: 0,
                refetchThrows: 0,
                sameKeyReplacements: 0,
                newKeyReplacements: 0,
                maxRetryInMs: 0,
                lastRetryInMs: undefined,
                lastRetryReason: undefined,
                lastError: undefined,
                lastEventType: event.type,
                newTableKey: undefined,
                healthEvidence: undefined,
                cancellationReason: undefined,
            };
        }

        if (!this.#state) {
            return;
        }

        this.#applyEvent(event);

        if (flush === 'after' || flush === 'before-and-after') {
            this.#flush();
        } else {
            this.#scheduleFlush();
        }
    }

    #applyEvent(event: EndOfDataRecoveryLogEvent): void {
        const state = this.#state;
        if (!state) {
            return;
        }

        state.pendingEvents = increment(state.pendingEvents);
        state.lastEventType = event.type;

        switch (event.type) {
            case 'incident-started':
                state.endOfDataEvents = increment(state.endOfDataEvents);
                state.phase = 'started';
                break;
            case 'end-of-data-coalesced':
                state.endOfDataEvents = increment(state.endOfDataEvents);
                state.coalescedEndOfDataEvents = increment(state.coalescedEndOfDataEvents);
                if (event.reason === 'in-flight') {
                    state.coalescedInFlightEvents = increment(state.coalescedInFlightEvents);
                } else {
                    state.coalescedScheduledEvents = increment(state.coalescedScheduledEvents);
                }
                break;
            case 'replacement-end-of-data':
                state.endOfDataEvents = increment(state.endOfDataEvents);
                break;
            case 'recovery-attempt':
                state.recoveryAttempts = Math.max(state.recoveryAttempts, event.attempt);
                break;
            case 'retry-scheduled':
                state.retriesScheduled = increment(state.retriesScheduled);
                state.recoveryAttempts = Math.max(state.recoveryAttempts, event.attempt - 1);
                state.lastRetryInMs = event.retryInMs;
                state.maxRetryInMs = Math.max(state.maxRetryInMs, event.retryInMs);
                state.lastRetryReason = event.reason;
                break;
            case 'refetch-failed':
                state.refetchFailures = increment(state.refetchFailures);
                if (event.failure === 'threw') {
                    state.refetchThrows = increment(state.refetchThrows);
                }
                state.lastError = event.error;
                break;
            case 'replacement-created':
                if (event.result === 'same-key') {
                    state.sameKeyReplacements = increment(state.sameKeyReplacements);
                } else {
                    state.newKeyReplacements = increment(state.newKeyReplacements);
                    state.newTableKey = event.newTableKey;
                }
                break;
            case 'still-recovering':
                state.phase = 'still-recovering';
                break;
            case 'healthy':
                state.phase = 'recovered';
                state.healthEvidence = event.evidence;
                break;
            case 'canceled':
                state.phase = 'canceled';
                state.cancellationReason = event.reason;
                break;
        }
    }

    #scheduleFlush(): void {
        if (!this.#state || this.#flushTimer !== undefined) {
            return;
        }
        this.#flushTimer = setTimeout(() => {
            this.#flushTimer = undefined;
            this.update({ type: 'still-recovering' }, 'after');
        }, this.#flushIntervalMs);
    }

    #flush(): void {
        const state = this.#state;
        if (!state || state.pendingEvents === 0) {
            return;
        }

        const phase = state.phase;
        const logData = {
            message: 'Table end-of-data recovery',
            event: 'table-end-of-data-recovery',
            phase,
            outcome: phase,
            tableModelId: this.#tableModelId,
            recoveryIncidentId: state.incidentId,
            tableKey: state.tableKey,
            debugIdentifier: state.debugIdentifier,
            durationMs: Math.max(0, this.#now() - state.startedAt),
            eventsSinceLastLog: state.pendingEvents,
            lastEventType: state.lastEventType,
            endOfDataEvents: state.endOfDataEvents,
            coalescedEndOfDataEvents: state.coalescedEndOfDataEvents,
            coalescedInFlightEvents: state.coalescedInFlightEvents,
            coalescedScheduledEvents: state.coalescedScheduledEvents,
            recoveryAttempts: state.recoveryAttempts,
            retriesScheduled: state.retriesScheduled,
            refetchFailures: state.refetchFailures,
            refetchThrows: state.refetchThrows,
            sameKeyReplacements: state.sameKeyReplacements,
            newKeyReplacements: state.newKeyReplacements,
            maxRetryInMs: state.maxRetryInMs,
            lastRetryInMs: state.lastRetryInMs,
            lastRetryReason: state.lastRetryReason,
            lastErrorName: state.lastError?.name,
            lastErrorMessage: state.lastError?.message,
            newTableKey: state.newTableKey,
            healthEvidence: state.healthEvidence,
            cancellationReason: state.cancellationReason,
        };

        if (phase === 'recovered') {
            this.#log.info(logData);
        } else if (phase === 'canceled') {
            this.#log.debug(logData);
        } else {
            this.#log.warn(logData);
        }

        state.pendingEvents = 0;
        if (phase === 'recovered' || phase === 'canceled') {
            this.#clearFlushTimer();
            this.#state = undefined;
            return;
        }

        state.phase = 'progress';
        this.#scheduleFlush();
    }

    #clearFlushTimer(): void {
        if (this.#flushTimer !== undefined) {
            clearTimeout(this.#flushTimer);
            this.#flushTimer = undefined;
        }
    }

    [Symbol.dispose](): void {
        if (this.#disposed) {
            return;
        }
        if (this.#state) {
            this.update({ type: 'canceled', reason: 'model-disposed' }, 'before-and-after');
        }
        this.#clearFlushTimer();
        this.#disposed = true;
    }
}
